// useCreatedBookings — the "Created today" log on /bookings.
//
// Lists bookings by when they were CREATED (not when they are booked for),
// newest first. Mirrors legacy operations/daily_bookings.php: the log shows
// everything created on the chosen day (default today, company-local day
// resolved server-side from created_from / created_to), and typing in the
// search box searches ALL records instead (legacy "all-records search").

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { useAuthStore } from '../../../store/authStore';
import type { BookingListResponse } from '../../../types/api';

const PAGE_LIMIT = 200;
const STALE_MS = 30_000;

export interface UseCreatedBookingsOptions {
  /** Created-on day, YYYY-MM-DD (ignored while `q` is set). */
  date: string;
  /** Free-text search across all records; empty = just the chosen day. */
  q?: string;
}

export interface CreatedBookingsResult {
  bookings: BookingListResponse['bookings'];
  total: number;
}

export function useCreatedBookings({ date, q = '' }: UseCreatedBookingsOptions) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const search = q.trim();

  return useQuery<CreatedBookingsResult>({
    queryKey: queryKeys.bookings.list({ view: 'created', date: search === '' ? date : null, q: search }),
    queryFn: async () => {
      const params: Record<string, string | number> = {
        sort: 'created_at_desc',
        limit: PAGE_LIMIT,
      };
      if (search === '') {
        params.created_from = date;
        params.created_to = date;
      } else {
        params.q = search;
      }
      const res = await apiClient.get<BookingListResponse>(endpoints.bookings.list, { params });
      return { bookings: res.data.bookings, total: res.data.pagination.total };
    },
    enabled: isAuthenticated,
    staleTime: STALE_MS,
    refetchInterval: search === '' ? 60_000 : false,
  });
}
